import {
  parseChatList,
  parseChatMessage,
  parseChatMessagePage,
  parseChatPrompts,
  parseChatStreamEvent,
  type ChatFeedback,
  type ChatList,
  type ChatMessage,
  type ChatMessagePage,
  type ChatPrompt,
  type ChatSendInput,
  type ChatStreamEvent,
} from "./chat";
import { readSse } from "@/shared/api/sse";
import {
  parseAppConfig,
  parseAsyncSummaryState,
  parseConnectExtensionResponse,
  parseMagicLinkResponse,
  parseMeetingDetail,
  parseMeetingSummary,
  parseTranscriptPage,
  parseTranscriptSearchResult,
  parseMeetingCore,
  parseMeetingFacets,
  parseMeetingList,
  parseMeetingListPage,
  parseOnboardingProgress,
  parseSessionList,
  parseSummarizeResponse,
  parseTokenResponse,
  parseUserProfile,
  parseWorkspaceInvitationPreview,
  parseWorkspaceList,
  parseWorkspaceMembership,
  parseSearchResponse,
  parseTodayResponse,
  parseReportRecord,
  parseReportVersionList,
  parseReportComparison,
  parseCustomerList,
  parseCustomerHistory,
  parseCustomerMeetingList,
  parseCustomer,
  parseMeetingAssociation,
  parseCommitmentSuggestion,
  parseCommitment,
  parseCommitmentList,
  parseCommitmentHistory,
  parseExternalTaskLinksResponse,
  parseCustomerMergeCandidates,
  parseCustomerMergePreview,
  parseCustomerMergeResponse,
  parseSpace,
  parseSpaceDashboard,
  parseSpaceList,
  parseShareGrant,
  parseShareGrantList,
  parseWorkspaceMemberList,
  parseSharedRecap,
  parseOkResponse,
  parseMeetingAudio,
  parseAudioTranscriptionRetry,
  parseConnectorList,
  parseConnectorInitiation,
  parseConnectorHealth,
  parsePrivacyResponse,
  parseDeletionResult,
  parseSampleWorkflow,
  parseExtensionHealth,
  parseWorkflowReview,
  parseBillingStatus,
  parseWorkflowRunList,
  parseWorkflowActivityList,
  parseWorkflowRetry,
  parseWorkflowReconciliation,
  parseWorkspacePolicy,
  parseWorkflowGoalReview,
  parseWorkflowActionMutationResult,
  parseUsageBudget,
  parseBillingAction,
} from "./guards";
import { safeRedirectPath } from "@/lib/errors";
import { HttpClient, resolveApiBaseUrl } from "@/shared/api/http-client";
import type {
  Locale,
  AppConfig,
  AsyncSummaryState,
  SummaryOptions,
  AuthSession,
  RevokeSessionsResponse,
  ConnectExtensionResponse,
  MagicLinkResponse,
  MeetingDetail,
  MeetingSummary,
  TranscriptPage,
  TranscriptSearchResult,
  MeetingCore,
  MeetingFacets,
  MeetingListItem,
  MeetingListPage,
  MeetingStatus,
  SummarizeResponse,
  TokenResponse,
  UserProfile,
  Workspace,
  WorkspaceInvitationPreview,
  WorkspaceMembership,
  OnboardingProgressResponse,
  SearchResponse,
  TodayResponse,
  ReportRecord,
  ReportVersionList,
  ReportComparison,
  StructuredReport,
  Customer,
  CustomerHistoryResponse,
  CustomerMeeting,
  MeetingAssociation,
  CommitmentSuggestion,
  Commitment,
  CommitmentListResponse,
  CommitmentHistoryResponse,
  ExternalTaskLink,
  CustomerMergeCandidatesResponse,
  CustomerMergePreview,
  CustomerMergeResponse,
  Space,
  SpaceDashboard,
  MeetingQuery,
  ShareGrant,
  ShareGrantListResponse,
  WorkspaceMemberListResponse,
  SharedRecap,
  OkResponse,
  ConnectorProvider,
  ConnectorListResponse,
  ConnectorInitiation,
  ConnectorHealthResponse,
  PrivacyResponse,
  DeletionResult,
  SampleWorkflow,
  ExtensionHealth,
  WorkflowReview,
  BillingStatus,
  WorkflowRunListResponse,
  WorkflowActivityListResponse,
  WorkflowRetryResponse,
  WorkflowReconciliationResponse,
  WorkspacePolicy,
  UsageBudget,
  BillingAction,
  WorkflowGoalCreateRequest,
  WorkflowGoalReview,
  WorkflowActionMutationRequest,
  WorkflowActionMutationResult,
  MeetingAudio,
  AudioTranscriptionRetry,
} from "./types";

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken?: () => string | null;
  onUnauthorized?: () => Promise<boolean>;
}

export interface RequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  auth?: boolean;
  responseType?: "json" | "text" | "blob" | "empty";
  workspaceId?: string;
  cursor?: string | null;
  idempotencyKey?: string;
}

export class ApiClient {
  private readonly http: HttpClient;

  constructor(options: ApiClientOptions) {
    this.http = new HttpClient({
      baseUrl: options.baseUrl,
      getBearerToken: options.getAccessToken,
      onUnauthorized: options.onUnauthorized,
    });
  }

  async getConfig(options?: RequestOptions): Promise<AppConfig> {
    const data = await this.request<unknown>("GET", "/v1/config", undefined, {
      ...options,
      auth: false,
    });
    return parseAppConfig(data);
  }

  async sendMagicLink(
    email: string,
    redirect: string,
    options?: RequestOptions,
  ): Promise<MagicLinkResponse> {
    const data = await this.request<unknown>(
      "POST",
      "/v1/auth/magic-link",
      { email, redirect: safeRedirectPath(redirect) },
      { ...options, auth: false },
    );
    return parseMagicLinkResponse(data);
  }

  async exchangeToken(
    provider: "email" | "refresh" | "google",
    credential: string,
    options?: RequestOptions,
  ): Promise<TokenResponse> {
    const data = await this.request<unknown>(
      "POST",
      "/v1/auth/token",
      { provider, credential },
      { ...options, auth: false },
    );
    return parseTokenResponse(data);
  }

  async getMe(options?: RequestOptions): Promise<UserProfile> {
    const data = await this.request<unknown>("GET", "/v1/me", undefined, options);
    return parseUserProfile(data);
  }

  async updateOnboardingProgress(
    onboardingProgress: number,
    options?: RequestOptions,
  ): Promise<OnboardingProgressResponse> {
    const data = await this.request<unknown>(
      "PATCH",
      "/v1/profile",
      { onboarding_progress: onboardingProgress },
      options,
    );
    return parseOnboardingProgress(data);
  }

  async listWorkspaces(options?: RequestOptions): Promise<Workspace[]> {
    const data = await this.request<unknown>("GET", "/v1/workspaces", undefined, options);
    return parseWorkspaceList(data);
  }

  async createWorkspace(name: string, options?: RequestOptions): Promise<Workspace> {
    const data = await this.request<unknown>("POST", "/v1/workspaces", { name }, options);
    return parseWorkspaceList({ workspaces: [data] })[0];
  }

  async acceptWorkspaceInvitation(
    token: string,
    options?: RequestOptions,
  ): Promise<WorkspaceMembership> {
    const data = await this.request<unknown>(
      "POST",
      `/v1/workspace-invitations/${encodeURIComponent(token)}/accept`,
      undefined,
      options,
    );
    return parseWorkspaceMembership(data);
  }

  async previewWorkspaceInvitation(
    token: string,
    options?: RequestOptions,
  ): Promise<WorkspaceInvitationPreview> {
    const data = await this.request<unknown>(
      "POST",
      "/v1/workspace-invitations/preview",
      { token },
      options,
    );
    return parseWorkspaceInvitationPreview(data);
  }

  async getToday(workspaceId: string, options?: RequestOptions): Promise<TodayResponse> {
    const data = await this.request<unknown>("GET", "/v1/today", undefined, { ...options, workspaceId });
    return parseTodayResponse(data);
  }

  async getReport(meetingId: string, options?: RequestOptions): Promise<ReportRecord> {
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(meetingId)}/report`, undefined, options);
    return parseReportRecord(data);
  }

  async getReportVersions(meetingId: string, options?: RequestOptions): Promise<ReportVersionList> {
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(meetingId)}/report/versions`, undefined, options);
    return parseReportVersionList(data);
  }

  async compareReport(meetingId: string, fromVersion: number, toVersion: number, options?: RequestOptions): Promise<ReportComparison> {
    const params = new URLSearchParams({ from_version: String(fromVersion), to_version: String(toVersion) });
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(meetingId)}/report/compare?${params.toString()}`, undefined, options);
    return parseReportComparison(data);
  }

  async updateReport(meetingId: string, report: StructuredReport, options?: RequestOptions): Promise<ReportRecord> {
    const data = await this.request<unknown>("PATCH", `/v1/meetings/${encodeURIComponent(meetingId)}/report`, report, options);
    return parseReportRecord(data);
  }

  async reviewReport(meetingId: string, version: number, decision: "approve", options?: RequestOptions): Promise<ReportRecord> {
    const data = await this.request<unknown>("POST", `/v1/meetings/${encodeURIComponent(meetingId)}/report/review`, { version, decision }, options);
    return parseReportRecord(data);
  }

  async downloadReportExport(meetingId: string, format: "json" | "markdown" | "txt", options?: RequestOptions): Promise<Blob> {
    return this.request<Blob>(
      "GET",
      `/v1/meetings/${encodeURIComponent(meetingId)}/report/export?format=${encodeURIComponent(format)}`,
      undefined,
      { ...options, responseType: "blob" },
    );
  }

  async search(query: string, workspaceId: string, options?: RequestOptions): Promise<SearchResponse> {
    const params = new URLSearchParams({ q: query });
    if (options?.cursor) params.set("cursor", options.cursor);
    const data = await this.request<unknown>("GET", `/v1/search?${params.toString()}`, undefined, { ...options, workspaceId });
    return parseSearchResponse(data);
  }

  async listCustomers(workspaceId: string, options?: RequestOptions): Promise<Customer[]> {
    const params = new URLSearchParams({ workspace_id: workspaceId });
    const data = await this.request<unknown>("GET", `/v1/customers?${params.toString()}`, undefined, { ...options, workspaceId });
    return parseCustomerList(data);
  }

  async listSpaces(workspaceId: string, filters?: { q?: string; status?: "active" | "archived" | "all"; sort?: "name" | "newest" | "updated" }, options?: RequestOptions): Promise<Space[]> {
    const params = new URLSearchParams();
    if (filters?.q) params.set("q", filters.q);
    if (filters?.status && filters.status !== "all") params.set("status", filters.status);
    if (filters?.sort) params.set("sort", filters.sort);
    const data = await this.request<unknown>("GET", `/v1/spaces${params.toString() ? `?${params}` : ""}`, undefined, { ...options, workspaceId });
    return parseSpaceList(data);
  }

  async createSpace(workspaceId: string, payload: { name: string; description?: string }, options?: RequestOptions): Promise<Space> {
    const data = await this.request<unknown>("POST", "/v1/spaces", payload, { ...options, workspaceId });
    return parseSpace(data);
  }

  async getSpace(spaceId: string, options?: RequestOptions): Promise<SpaceDashboard> {
    const data = await this.request<unknown>("GET", `/v1/spaces/${encodeURIComponent(spaceId)}`, undefined, options);
    return parseSpaceDashboard(data);
  }

  async updateSpace(spaceId: string, patch: { expected_version: number; name?: string; description?: string; status?: "active" | "archived" }, options?: RequestOptions): Promise<Space> {
    const data = await this.request<unknown>("PATCH", `/v1/spaces/${encodeURIComponent(spaceId)}`, patch, options);
    return parseSpace(data);
  }

  async replaceMeetingSpaces(meetingId: string, spaceIds: string[], options?: RequestOptions): Promise<Space[]> {
    const data = await this.request<unknown>("PUT", `/v1/meetings/${encodeURIComponent(meetingId)}/spaces`, { space_ids: spaceIds }, options);
    return parseSpaceList({ items: Array.isArray((data as { spaces?: unknown }).spaces) ? (data as { spaces: unknown[] }).spaces : data });
  }

  async replaceCommitmentSpaces(commitmentId: string, spaceIds: string[], options?: RequestOptions): Promise<Space[]> {
    const data = await this.request<unknown>("PUT", `/v1/commitments/${encodeURIComponent(commitmentId)}/spaces`, { space_ids: spaceIds }, options);
    return parseSpaceList({ items: Array.isArray((data as { spaces?: unknown }).spaces) ? (data as { spaces: unknown[] }).spaces : data });
  }

  async createCustomer(workspaceId: string, name: string, domain = "", options?: RequestOptions): Promise<Customer> {
    const data = await this.request<unknown>("POST", "/v1/customers", { workspace_id: workspaceId, name, domain }, { ...options, workspaceId });
    return parseCustomer(data);
  }

  async updateCustomer(customerId: string, name: string, domain = "", options?: RequestOptions): Promise<Customer> {
    const data = await this.request<unknown>("PATCH", `/v1/customers/${encodeURIComponent(customerId)}`, { name, domain }, options);
    return parseCustomer(data);
  }

  async listCustomerMergeCandidates(customerId: string, options?: RequestOptions): Promise<CustomerMergeCandidatesResponse> {
    const data = await this.request<unknown>("GET", `/v1/customers/${encodeURIComponent(customerId)}/merge-candidates`, undefined, options);
    return parseCustomerMergeCandidates(data);
  }

  async previewCustomerMerge(sourceCustomerId: string, targetCustomerId: string, options?: RequestOptions): Promise<CustomerMergePreview> {
    const data = await this.request<unknown>("POST", "/v1/customers/merge/preview", { source_customer_id: sourceCustomerId, target_customer_id: targetCustomerId }, options);
    return parseCustomerMergePreview(data);
  }

  async confirmCustomerMerge(sourceCustomerId: string, targetCustomerId: string, expectedSourceVersion: number, expectedTargetVersion: number, options?: RequestOptions): Promise<CustomerMergeResponse> {
    const data = await this.request<unknown>("POST", "/v1/customers/merge/confirm", { source_customer_id: sourceCustomerId, target_customer_id: targetCustomerId, expected_source_version: expectedSourceVersion, expected_target_version: expectedTargetVersion }, options);
    return parseCustomerMergeResponse(data);
  }

  async getCustomer(customerId: string, workspaceId: string, options?: RequestOptions): Promise<Customer> {
    const data = await this.request<unknown>("GET", `/v1/customers/${encodeURIComponent(customerId)}`, undefined, { ...options, workspaceId });
    return parseCustomer(data);
  }

  async listCustomerMeetings(customerId: string, workspaceId: string, options?: RequestOptions): Promise<CustomerMeeting[]> {
    const data = await this.request<unknown>("GET", `/v1/customers/${encodeURIComponent(customerId)}/meetings`, undefined, { ...options, workspaceId });
    return parseCustomerMeetingList(data);
  }

  async getCustomerHistory(customerId: string, workspaceId: string, options?: RequestOptions): Promise<CustomerHistoryResponse> {
    const data = await this.request<unknown>("GET", `/v1/customers/${encodeURIComponent(customerId)}/history`, undefined, { ...options, workspaceId });
    return parseCustomerHistory(data);
  }

  async listWorkspaceMembers(workspaceId: string, options?: RequestOptions): Promise<WorkspaceMemberListResponse> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/members`, undefined, { ...options, workspaceId });
    return parseWorkspaceMemberList(data);
  }

  async updateWorkspaceMemberRole(workspaceId: string, userId: string, role: "admin" | "member" | "billing_admin", options?: RequestOptions): Promise<OkResponse> {
    const data = await this.request<unknown>("PATCH", `/v1/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`, { role }, { ...options, workspaceId });
    return parseOkResponse(data);
  }

  async revokeWorkspaceMember(workspaceId: string, userId: string, options?: RequestOptions): Promise<OkResponse> {
    const data = await this.request<unknown>("DELETE", `/v1/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`, undefined, { ...options, workspaceId });
    return parseOkResponse(data);
  }

  async associateMeetingCustomer(meetingId: string, customerId: string, options?: RequestOptions): Promise<MeetingAssociation> {
    const data = await this.request<unknown>("POST", `/v1/meetings/${encodeURIComponent(meetingId)}/customers`, { customer_id: customerId }, options);
    return parseMeetingAssociation(data);
  }

  async removeMeetingCustomer(meetingId: string, customerId: string, options?: RequestOptions): Promise<OkResponse> {
    const data = await this.request<unknown>("DELETE", `/v1/meetings/${encodeURIComponent(meetingId)}/customers/${encodeURIComponent(customerId)}`, undefined, options);
    return parseOkResponse(data);
  }

  async suggestMeetingCustomer(meetingId: string, customerId: string, confidence: number, options?: RequestOptions): Promise<MeetingAssociation> {
    const data = await this.request<unknown>("POST", `/v1/meetings/${encodeURIComponent(meetingId)}/customer-suggestions`, { customer_id: customerId, confidence }, options);
    return parseMeetingAssociation(data);
  }

  async createCommitmentSuggestion(meetingId: string, payload: { customer_id?: string; source_report_id?: string; source_action_id?: string; text: string; due_phrase?: string; evidence?: Array<Record<string, unknown>> }, options?: RequestOptions): Promise<CommitmentSuggestion> {
    const data = await this.request<unknown>("POST", `/v1/meetings/${encodeURIComponent(meetingId)}/commitment-suggestions`, payload, options);
    return parseCommitmentSuggestion(data);
  }

  async confirmCommitmentSuggestion(suggestionId: string, timezone: string, options?: RequestOptions): Promise<Commitment> {
    const data = await this.request<unknown>("POST", `/v1/commitment-suggestions/${encodeURIComponent(suggestionId)}/confirm`, { timezone }, options);
    return parseCommitment(data);
  }

  async updateCommitment(commitmentId: string, status: "open" | "completed" | "cancelled", options?: RequestOptions): Promise<Commitment> {
    const data = await this.request<unknown>("PATCH", `/v1/commitments/${encodeURIComponent(commitmentId)}`, { status }, options);
    return parseCommitment(data);
  }

  async editCommitment(commitmentId: string, patch: { expected_version: number; text?: string; owner_user_id?: string; due_phrase?: string; due_at?: string | null; timezone?: string; status?: Commitment["status"] }, options?: RequestOptions): Promise<Commitment> {
    const data = await this.request<unknown>("PATCH", `/v1/commitments/${encodeURIComponent(commitmentId)}`, patch, options);
    return parseCommitment(data);
  }

  async listExternalTaskLinks(commitmentId: string, options?: RequestOptions): Promise<{ commitment_id: string; items: ExternalTaskLink[] }> {
    const data = await this.request<unknown>("GET", `/v1/commitments/${encodeURIComponent(commitmentId)}/external-task-links`, undefined, options);
    return parseExternalTaskLinksResponse(data);
  }

  async addExternalTaskLink(commitmentId: string, payload: { expected_version: number; provider: string; external_task_id: string; url?: string }, options?: RequestOptions): Promise<Commitment> {
    const data = await this.request<unknown>("POST", `/v1/commitments/${encodeURIComponent(commitmentId)}/external-task-links`, payload, options);
    return parseCommitment(data);
  }

  async removeExternalTaskLink(commitmentId: string, linkId: string, expectedVersion: number, options?: RequestOptions): Promise<Commitment> {
    const data = await this.request<unknown>("DELETE", `/v1/commitments/${encodeURIComponent(commitmentId)}/external-task-links/${encodeURIComponent(linkId)}`, { expected_version: expectedVersion }, options);
    return parseCommitment(data);
  }

  async listCommitments(workspaceId: string, filters?: { status?: Commitment["status"]; customerId?: string; spaceId?: string }, options?: RequestOptions): Promise<CommitmentListResponse> {
    const params = new URLSearchParams({ workspace_id: workspaceId });
    if (filters?.status) params.set("status", filters.status);
    if (filters?.customerId) params.set("customer_id", filters.customerId);
    if (filters?.spaceId) params.set("space", filters.spaceId);
    const data = await this.request<unknown>("GET", `/v1/commitments?${params.toString()}`, undefined, { ...options, workspaceId });
    return parseCommitmentList(data);
  }

  async getCommitment(commitmentId: string, options?: RequestOptions): Promise<Commitment> {
    const data = await this.request<unknown>("GET", `/v1/commitments/${encodeURIComponent(commitmentId)}`, undefined, options);
    return parseCommitment(data);
  }

  async getCommitmentHistory(commitmentId: string, options?: RequestOptions): Promise<CommitmentHistoryResponse> {
    const data = await this.request<unknown>("GET", `/v1/commitments/${encodeURIComponent(commitmentId)}/history`, undefined, options);
    return parseCommitmentHistory(data);
  }

  async createShare(meetingId: string, recipientUserId: string, capability: "recap" | "full_transcript" | "audio" = "recap", expiresAt?: string, options?: RequestOptions): Promise<ShareGrant> {
    const data = await this.request<unknown>("POST", `/v1/meetings/${encodeURIComponent(meetingId)}/shares`, { recipient_user_id: recipientUserId, capability, ...(expiresAt ? { expires_at: expiresAt } : {}) }, options);
    return parseShareGrant(data);
  }

  async listShares(meetingId: string, options?: RequestOptions): Promise<ShareGrantListResponse> {
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(meetingId)}/shares`, undefined, options);
    return parseShareGrantList(data);
  }

  async revokeShare(meetingId: string, shareId: string, options?: RequestOptions): Promise<{ contract_version: "v1"; share_id: string; revoked: true }> {
    const data = await this.request<unknown>("DELETE", `/v1/meetings/${encodeURIComponent(meetingId)}/shares/${encodeURIComponent(shareId)}`, undefined, options);
    if (!data || typeof data !== "object" || (data as Record<string, unknown>).contract_version !== "v1" || (data as Record<string, unknown>).share_id !== shareId || (data as Record<string, unknown>).revoked !== true) throw new Error("Invalid share revoke response");
    return { contract_version: "v1", share_id: shareId, revoked: true };
  }

  async getSharedRecap(meetingId: string, options?: RequestOptions): Promise<SharedRecap> {
    const data = await this.request<unknown>("GET", `/v1/shared/meetings/${encodeURIComponent(meetingId)}/recap`, undefined, options);
    return parseSharedRecap(data);
  }

  async renameMeeting(meetingId: string, title: string, options?: RequestOptions): Promise<MeetingCore> {
    const data = await this.request<unknown>("PATCH", `/v1/meetings/${encodeURIComponent(meetingId)}`, { title }, options);
    return parseMeetingCore(data);
  }

  async deleteMeeting(meetingId: string, options?: RequestOptions): Promise<void> {
    await this.request<unknown>("DELETE", `/v1/meetings/${encodeURIComponent(meetingId)}`, undefined, options);
  }

  async listMeetingChats(meetingId: string, options?: RequestOptions & { limit?: number; cursor?: string | null }): Promise<ChatList> {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set("limit", String(options.limit));
    if (options?.cursor) params.set("cursor", options.cursor);
    const query = params.toString();
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(meetingId)}/chats${query ? `?${query}` : ""}`, undefined, options);
    return parseChatList(data);
  }

  async listChatMessages(meetingId: string, chatId: string, options?: RequestOptions & { limit?: number; cursor?: string | null }): Promise<ChatMessagePage> {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set("limit", String(options.limit));
    if (options?.cursor) params.set("cursor", options.cursor);
    const query = params.toString();
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(meetingId)}/chats/${encodeURIComponent(chatId)}/messages${query ? `?${query}` : ""}`, undefined, options);
    return parseChatMessagePage(data);
  }

  async deleteMeetingChat(meetingId: string, chatId: string, options?: RequestOptions): Promise<void> {
    await this.request<unknown>("DELETE", `/v1/meetings/${encodeURIComponent(meetingId)}/chats/${encodeURIComponent(chatId)}`, undefined, { ...options, responseType: "empty" });
  }

  /** Sets (or, with null, clears) the thumbs rating of an answer. */
  async setChatMessageFeedback(meetingId: string, chatId: string, messageId: string, rating: ChatFeedback | null, reason?: string, options?: RequestOptions): Promise<ChatMessage> {
    const data = await this.request<unknown>(
      "PUT",
      `/v1/meetings/${encodeURIComponent(meetingId)}/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/feedback`,
      reason ? { rating, reason } : { rating },
      options,
    );
    return parseChatMessage(data);
  }

  async listChatPrompts(locale: Locale, options?: RequestOptions): Promise<ChatPrompt[]> {
    const data = await this.request<unknown>("GET", `/v1/chat-prompts?locale=${locale}`, undefined, options);
    return parseChatPrompts(data);
  }

  /**
   * Starts a chat with its first question and streams the answer. Problems found
   * before streaming starts (limits, validation, access) reject the returned
   * promise with an ApiError; once it resolves, failures arrive as an `error` event.
   * `clientMessageId` must be new per send: repeating it returns the stored answer.
   */
  openNewChatStream(meetingId: string, input: ChatSendInput, clientMessageId: string, options?: RequestOptions): Promise<AsyncGenerator<ChatStreamEvent>> {
    return this.openChatStream(`/v1/meetings/${encodeURIComponent(meetingId)}/chats`, chatSendBody(input, clientMessageId), options);
  }

  /** Streams the answer to a follow-up question in an existing chat. */
  openFollowUpStream(meetingId: string, chatId: string, input: ChatSendInput, clientMessageId: string, options?: RequestOptions): Promise<AsyncGenerator<ChatStreamEvent>> {
    return this.openChatStream(`/v1/meetings/${encodeURIComponent(meetingId)}/chats/${encodeURIComponent(chatId)}/messages`, chatSendBody(input, clientMessageId), options);
  }

  /** Regenerates a failed or stopped answer in place. */
  openRetryStream(meetingId: string, chatId: string, messageId: string, options?: RequestOptions): Promise<AsyncGenerator<ChatStreamEvent>> {
    return this.openChatStream(`/v1/meetings/${encodeURIComponent(meetingId)}/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/retry`, undefined, options);
  }

  private async openChatStream(path: string, body: unknown, options: RequestOptions = {}): Promise<AsyncGenerator<ChatStreamEvent>> {
    const response = await this.http.openStream("POST", withApiPrefix(path), body, {
      signal: options.signal,
      timeoutMs: options.timeoutMs,
      workspaceId: options.workspaceId,
    });
    return chatEvents(response.body as ReadableStream<Uint8Array>, options.signal);
  }

  async listConnectors(workspaceId: string, options?: RequestOptions): Promise<ConnectorListResponse> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/connectors`, undefined, { ...options, workspaceId });
    return parseConnectorList(data);
  }

  async initiateConnector(workspaceId: string, provider: ConnectorProvider, request: { account_type: string; api_version: string; scopes: string[] }, options?: RequestOptions): Promise<ConnectorInitiation> {
    const data = await this.request<unknown>("POST", `/v1/workspaces/${encodeURIComponent(workspaceId)}/connectors/${provider}/connect`, request, { ...options, workspaceId });
    return parseConnectorInitiation(data);
  }

  async revokeConnector(workspaceId: string, connectorId: string, options?: RequestOptions): Promise<OkResponse> {
    const data = await this.request<unknown>("POST", `/v1/workspaces/${encodeURIComponent(workspaceId)}/connectors/${encodeURIComponent(connectorId)}/revoke`, undefined, { ...options, workspaceId });
    return parseOkResponse(data);
  }

  async getConnectorHealth(workspaceId: string, connectorId: string, options?: RequestOptions): Promise<ConnectorHealthResponse> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/connectors/${encodeURIComponent(connectorId)}/health`, undefined, { ...options, workspaceId });
    return parseConnectorHealth(data);
  }

  async getWorkflowReview(workspaceId: string, jobId: string, options?: RequestOptions): Promise<WorkflowReview> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/workflow/jobs/${encodeURIComponent(jobId)}`, undefined, { ...options, workspaceId });
    return parseWorkflowReview(data);
  }

  async listWorkflowRuns(workspaceId: string, options?: RequestOptions & { state?: string; limit?: number }): Promise<WorkflowRunListResponse> {
    const params = new URLSearchParams();
    if (options?.state) params.set("state", options.state);
    if (options?.cursor) params.set("cursor", options.cursor);
    if (options?.limit != null) params.set("limit", String(options.limit));
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/workflow/runs${params.toString() ? `?${params.toString()}` : ""}`, undefined, { ...options, workspaceId });
    return parseWorkflowRunList(data);
  }

  async listWorkflowActivity(workspaceId: string, options?: RequestOptions & { limit?: number }): Promise<WorkflowActivityListResponse> {
    const params = new URLSearchParams();
    if (options?.cursor) params.set("cursor", options.cursor);
    if (options?.limit != null) params.set("limit", String(options.limit));
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/activity${params.toString() ? `?${params.toString()}` : ""}`, undefined, { ...options, workspaceId });
    return parseWorkflowActivityList(data);
  }

  async retryRemainingWorkflow(workspaceId: string, jobId: string, options?: RequestOptions): Promise<WorkflowRetryResponse> {
    const data = await this.request<unknown>("POST", `/v1/workspaces/${encodeURIComponent(workspaceId)}/workflow/jobs/${encodeURIComponent(jobId)}/retry-remaining`, undefined, { ...options, workspaceId, idempotencyKey: options?.idempotencyKey ?? `web-retry-${jobId}` });
    return parseWorkflowRetry(data);
  }

  async getWorkflowReconciliation(workspaceId: string, jobId: string, options?: RequestOptions): Promise<WorkflowReconciliationResponse> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/workflow/jobs/${encodeURIComponent(jobId)}/reconciliation`, undefined, { ...options, workspaceId });
    return parseWorkflowReconciliation(data);
  }

  async getWorkspacePolicy(workspaceId: string, options?: RequestOptions): Promise<WorkspacePolicy> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/policy`, undefined, { ...options, workspaceId });
    return parseWorkspacePolicy(data);
  }

  async updateWorkspacePolicy(workspaceId: string, patch: { expected_version: number; default_visibility?: "private" | "workspace"; crm_notes_and_tasks?: boolean }, options?: RequestOptions): Promise<WorkspacePolicy> {
    const data = await this.request<unknown>("PATCH", `/v1/workspaces/${encodeURIComponent(workspaceId)}/policy`, { expected_version: patch.expected_version, ...(patch.default_visibility ? { preferences: { default_visibility: patch.default_visibility } } : {}), ...(patch.crm_notes_and_tasks === undefined ? {} : { crm_notes_and_tasks: patch.crm_notes_and_tasks }) }, { ...options, workspaceId });
    return parseWorkspacePolicy(data);
  }

  async createWorkflowGoal(workspaceId: string, request: WorkflowGoalCreateRequest, options?: RequestOptions): Promise<WorkflowGoalReview> {
    const data = await this.request<unknown>("POST", `/v1/workspaces/${encodeURIComponent(workspaceId)}/workflow/goals`, request, { ...options, workspaceId, idempotencyKey: options?.idempotencyKey ?? request.idempotency_key });
    return parseWorkflowGoalReview(data);
  }

  async getWorkflowGoalReview(workspaceId: string, goalId: string, options?: RequestOptions): Promise<WorkflowGoalReview> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/workflow/goals/${encodeURIComponent(goalId)}`, undefined, { ...options, workspaceId });
    return parseWorkflowGoalReview(data);
  }

  async mutateWorkflowAction(workspaceId: string, actionId: string, request: WorkflowActionMutationRequest, options?: RequestOptions): Promise<WorkflowActionMutationResult> {
    const data = await this.request<unknown>("PATCH", `/v1/workspaces/${encodeURIComponent(workspaceId)}/workflow/actions/${encodeURIComponent(actionId)}`, request, { ...options, workspaceId });
    return parseWorkflowActionMutationResult(data);
  }

  async updateWorkflowRun(workspaceId: string, jobId: string, action: "pause" | "resume" | "cancel", options?: RequestOptions): Promise<{ status: string }> {
    const data = await this.request<unknown>("POST", `/v1/workspaces/${encodeURIComponent(workspaceId)}/workflow/jobs/${encodeURIComponent(jobId)}/${action}`, undefined, { ...options, workspaceId });
    if (!data || typeof data !== "object" || typeof (data as Record<string, unknown>).status !== "string") throw new Error("Invalid workflow operation response");
    return { status: (data as Record<string, string>).status };
  }

  async approveWorkflowAction(workspaceId: string, actionId: string, request: { payload_hash: string; source_version: string; source_digest: string; policy_version: number; expires_at?: string }, options?: RequestOptions): Promise<{ action_id: string; status: string; effect_id?: string }> {
    const path = "/v1/workspaces/" + encodeURIComponent(workspaceId) + "/workflow/actions/" + encodeURIComponent(actionId) + "/approve";
    const data = await this.request<unknown>("POST", path, request, { ...options, workspaceId });
    if (!data || typeof data !== "object" || typeof (data as Record<string, unknown>).action_id !== "string" || typeof (data as Record<string, unknown>).status !== "string") throw new Error("Invalid workflow approval response");
    return { action_id: (data as Record<string, string>).action_id, status: (data as Record<string, string>).status, ...((data as Record<string, unknown>).effect_id === undefined ? {} : { effect_id: String((data as Record<string, unknown>).effect_id) }) };
  }

  async getBillingStatus(workspaceId: string, options?: RequestOptions): Promise<BillingStatus> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/billing`, undefined, { ...options, workspaceId });
    return parseBillingStatus(data);
  }

  async getUsageBudget(workspaceId: string, capability: "summary" | "transcription" | "workflow" = "summary", options?: RequestOptions): Promise<UsageBudget> {
    const data = await this.request<unknown>("GET", `/v1/workspaces/${encodeURIComponent(workspaceId)}/usage?capability=${encodeURIComponent(capability)}`, undefined, { ...options, workspaceId });
    return parseUsageBudget(data);
  }

  async startBillingAction(workspaceId: string, action: "checkout" | "portal", options?: RequestOptions): Promise<BillingAction> {
    const data = await this.request<unknown>("POST", `/v1/workspaces/${encodeURIComponent(workspaceId)}/billing/${action}`, undefined, { ...options, workspaceId });
    return parseBillingAction(data);
  }

  async getPrivacy(options?: RequestOptions): Promise<PrivacyResponse> {
    const data = await this.request<unknown>("GET", "/v1/privacy", undefined, options);
    return parsePrivacyResponse(data);
  }

  async updatePrivacy(patch: { ai_processing_enabled?: boolean; retention_days?: number }, options?: RequestOptions): Promise<PrivacyResponse> {
    const data = await this.request<unknown>("PATCH", "/v1/privacy", patch, options);
    return parsePrivacyResponse(data);
  }

  async exportPrivacy(options?: RequestOptions): Promise<Blob> {
    return this.request<Blob>("GET", "/v1/privacy/export", undefined, { ...options, responseType: "blob" });
  }

  async deleteAccount(options?: RequestOptions): Promise<DeletionResult> {
    const data = await this.request<unknown>("POST", "/v1/privacy/delete", { confirmation: "DELETE MY ACCOUNT" }, options);
    return parseDeletionResult(data);
  }

  async getSampleWorkflow(workspaceId: string, options?: RequestOptions): Promise<SampleWorkflow> {
    const data = await this.request<unknown>("POST", `/v1/workspaces/${encodeURIComponent(workspaceId)}/sample-workflow`, undefined, {
      ...options,
      workspaceId,
      idempotencyKey: options?.idempotencyKey ?? `web-onboarding-${workspaceId}`,
    });
    return parseSampleWorkflow(data);
  }

  async getExtensionHealth(options?: RequestOptions): Promise<ExtensionHealth> {
    const data = await this.request<unknown>("GET", "/v1/auth/connect-extension/health", undefined, options);
    return parseExtensionHealth(data);
  }

  async listMeetings(options?: RequestOptions & { status?: MeetingStatus; limit?: number; offset?: number }): Promise<MeetingListItem[]> {
    const params = new URLSearchParams();
    if (options?.status) params.set("status", options.status);
    if (options?.limit != null) params.set("limit", String(options.limit));
    if (options?.offset != null) params.set("offset", String(options.offset));
    const query = params.toString();
    const data = await this.request<unknown>("GET", `/v1/meetings${query ? `?${query}` : ""}`, undefined, options);
    return parseMeetingList(data);
  }

  // includeTotal asks the server to count every match; it is only honoured on
  // the first page (no cursor) and costs a scan, so request it sparingly.
  async listMeetingsPage(options?: RequestOptions & MeetingQuery & { status?: MeetingStatus; statuses?: MeetingStatus[]; query?: string; includeTotal?: boolean }): Promise<MeetingListPage> {
    const params = new URLSearchParams();
    if (options?.status) params.set("status", options.status);
    options?.statuses?.forEach((status) => params.append("status", status));
    if (options?.query) params.set("q", options.query);
    if (options?.q) params.set("q", options.q);
    options?.spaces?.forEach((space) => params.append("space", space));
    options?.platforms?.forEach((platform) => params.append("platform", platform));
    options?.overviews?.forEach((overview) => params.append("overview", overview));
    if (options?.from) params.set("from", options.from);
    if (options?.to) params.set("to", options.to);
    if (options?.sort) params.set("sort", options.sort);
    if (options?.cursor) params.set("cursor", options.cursor);
    if (options?.limit != null) params.set("limit", String(options.limit));
    if (options?.includeTotal) params.set("include_total", "true");
    const query = params.toString();
    const data = await this.request<unknown>("GET", `/v1/meetings${query ? `?${query}` : ""}`, undefined, options);
    return parseMeetingListPage(data);
  }

  async getMeeting(id: string, options?: RequestOptions): Promise<MeetingDetail> {
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(id)}`, undefined, options);
    return parseMeetingDetail(data);
  }

  /**
   * Playback data for a meeting's audio. The presigned `playback.url` stays in
   * memory only: callers must not persist it or send it to logs or analytics.
   * Resolves null when the server reports a status this client does not know.
   */
  async getMeetingAudio(id: string, options?: RequestOptions): Promise<MeetingAudio | null> {
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(id)}/audio`, undefined, options);
    return parseMeetingAudio(data);
  }

  /** Queues transcription from audio again; 409 when it is turned off or has not failed. */
  async retryAudioTranscription(id: string, options?: RequestOptions): Promise<AudioTranscriptionRetry> {
    const data = await this.request<unknown>("POST", `/v1/meetings/${encodeURIComponent(id)}/audio/transcription/retry`, undefined, options);
    return parseAudioTranscriptionRetry(data);
  }

  /** Deletes a meeting's audio; the transcript and recap stay. Idempotent. */
  async deleteMeetingAudio(id: string, options?: RequestOptions): Promise<void> {
    await this.request<void>("DELETE", `/v1/meetings/${encodeURIComponent(id)}/audio`, undefined, { ...options, responseType: "empty" });
  }

  async getMeetingFacets(options?: RequestOptions): Promise<MeetingFacets> {
    const data = await this.request<unknown>("GET", "/v1/meetings/facets", undefined, options);
    return parseMeetingFacets(data);
  }

  async getMeetingTranscripts(
    id: string,
    options?: RequestOptions & { cursor?: string | null; limit?: number; updatedSince?: string },
  ): Promise<TranscriptPage> {
    const params = new URLSearchParams();
    if (options?.cursor) params.set("cursor", options.cursor);
    // Only the lines written or revised since then; the server rejects it together with a cursor.
    if (options?.updatedSince) params.set("updated_since", options.updatedSince);
    if (options?.limit != null) params.set("limit", String(options.limit));
    const query = params.toString();
    const path = `/v1/meetings/${encodeURIComponent(id)}/transcripts${query ? `?${query}` : ""}`;
    const data = await this.request<unknown>("GET", path, undefined, options);
    return parseTranscriptPage(data);
  }

  /**
   * Searches every line of a meeting. The query is sent as typed, untrimmed: a
   * trailing space ends the last word, which turns off prefix matching for it.
   */
  async searchMeetingTranscripts(
    id: string,
    query: string,
    options?: RequestOptions & { limit?: number },
  ): Promise<TranscriptSearchResult> {
    const params = new URLSearchParams({ q: query });
    if (options?.limit != null) params.set("limit", String(options.limit));
    const path = `/v1/meetings/${encodeURIComponent(id)}/transcripts/search?${params.toString()}`;
    const data = await this.request<unknown>("GET", path, undefined, options);
    return parseTranscriptSearchResult(data);
  }

  async getMeetingSummary(id: string, options?: RequestOptions): Promise<MeetingSummary | null> {
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(id)}/summary`, undefined, options);
    return parseMeetingSummary(data);
  }

  async summarize(meetingId: string, options?: RequestOptions): Promise<SummarizeResponse> {
    const data = await this.request<unknown>(
      "POST",
      "/v1/summarize",
      { meeting_id: meetingId },
      options,
    );
    return parseSummarizeResponse(data);
  }

  async requestSummary(meetingId: string, regenerate = false, summaryOptions?: SummaryOptions, options?: RequestOptions): Promise<AsyncSummaryState> {
    const suffix = regenerate ? "/regenerate" : "";
    const data = await this.request<unknown>("POST", `/v1/meetings/${encodeURIComponent(meetingId)}/summary${suffix}`, summaryOptions, options);
    return parseAsyncSummaryState(data);
  }

  async getSummaryStatus(meetingId: string, options?: RequestOptions): Promise<AsyncSummaryState | null> {
    const data = await this.request<unknown>("GET", `/v1/meetings/${encodeURIComponent(meetingId)}/summary`, undefined, options);
    if (data === null) return null;
    return parseAsyncSummaryState(data);
  }

  async downloadSummary(meetingId: string, options?: RequestOptions): Promise<string> {
    return this.request<string>(
      "GET",
      `/v1/meetings/${encodeURIComponent(meetingId)}/summary/content`,
      undefined,
      { ...options, responseType: "text" },
    );
  }

  async createConnectCode(
    clientId: string,
    extensionId: string,
    workspaceId: string,
    options?: RequestOptions,
  ): Promise<ConnectExtensionResponse> {
    const data = await this.request<unknown>(
      "POST",
      "/v1/auth/connect-extension",
      { client_id: clientId, extension_id: extensionId, workspace_id: workspaceId },
      options,
    );
    return parseConnectExtensionResponse(data);
  }

  async listSessions(options?: RequestOptions): Promise<AuthSession[]> {
    const data = await this.request<unknown>("GET", "/v1/me/sessions", undefined, options);
    return parseSessionList(data);
  }

  async revokeSession(sessionId: string, options?: RequestOptions): Promise<void> {
    await this.request<void>(
      "DELETE",
      `/v1/me/sessions/${encodeURIComponent(sessionId)}`,
      undefined,
      { ...options, responseType: "empty" },
    );
  }

  async revokeOtherSessions(options?: RequestOptions): Promise<number> {
    const data = await this.request<RevokeSessionsResponse>(
      "DELETE",
      "/v1/me/sessions?except=current",
      undefined,
      options,
    );
    return data.revoked_sessions;
  }

  async signOut(_refreshToken?: string, options?: RequestOptions): Promise<void> {
    await this.request<void>(
      "DELETE",
      "/v1/auth/session",
      undefined,
      { ...options, auth: false, responseType: "empty" },
    );
  }

  private async request<T>(
    method: string,
    path: string,
    body: unknown | undefined,
    options: RequestOptions = {},
  ): Promise<T> {
    return this.http.request<T>(method, withApiPrefix(path), body, {
      signal: options.signal,
      timeoutMs: options.timeoutMs,
      auth: options.auth,
      responseType: options.responseType,
      workspaceId: options.workspaceId,
      idempotencyKey: options.idempotencyKey,
      // The API returns successful JSON in its standard { message, data, meta }
      // envelope. HttpClient preserves unwrapped/raw responses too, so this keeps
      // compatibility with endpoints that have not adopted the envelope yet.
      unwrapData: options.responseType !== "text" && options.responseType !== "blob",
    });
  }
}

function chatSendBody(input: ChatSendInput, clientMessageId: string): Record<string, string> {
  return "promptId" in input
    ? { prompt_id: input.promptId, client_message_id: clientMessageId }
    : { content: input.content, client_message_id: clientMessageId };
}

async function* chatEvents(body: ReadableStream<Uint8Array>, signal?: AbortSignal): AsyncGenerator<ChatStreamEvent> {
  for await (const sse of readSse(body, signal)) {
    const event = parseChatStreamEvent(sse.event, sse.data);
    if (event) yield event;
  }
}

function withApiPrefix(path: string): string {
  if (path.startsWith("/api/") || path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return path.startsWith("/") ? `/api${path}` : `/api/${path}`;
}

export { resolveApiBaseUrl };
